/**
 * SkillDelivery Platform
 *
 * Copyright © 2025-2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * x-Connect Goals Component
 * Phase 0.8 P5: Now reads from unified /api/goals endpoint (profile_goals table)
 * Same goals appear in both Workspace GoalsSection and x-Connect Goals tab
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Target, Plus, X, AlertCircle, Loader } from 'lucide-react';
import api from '../../../services/api';
import { goalTypesByEntity, getGoalTypesForEntity } from '../../../config/goalTypes';
import './XConnect.css';

const XConnectGoals = ({ user, entityType, vertical, onGoalChange }) => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Form fields mapped to profile_goals schema
  const [newGoal, setNewGoal] = useState({
    goal_type: '',      // maps to goalCategory
    goal_text: '',      // maps to title
    goal_description: '',
    priority: 5
  });

  // Fetch goals from unified /api/goals endpoint (profile_goals table)
  const fetchGoals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/goals');
      if (response?.success) {
        setGoals(response.goals || []);
      } else {
        console.warn('[XConnectGoals] API returned unsuccessful response:', response);
        setError(response?.error || 'Failed to load goals');
      }
    } catch (err) {
      console.error('[XConnectGoals] Error fetching goals:', err);
      setError(err.message || 'Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  // Add goal using unified /api/goals endpoint
  const handleAddGoal = async (e) => {
    e.preventDefault();
    if (!newGoal.goal_type || !newGoal.goal_text) {
      setError('Please select a goal type and enter a title');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const response = await api.post('/goals', {
        goal_type: newGoal.goal_type,
        goal_text: newGoal.goal_text,
        goal_description: newGoal.goal_description,
        priority: newGoal.priority,
        vertical: vertical || 'WMS'
      });

      if (response?.success) {
        setGoals(prev => [response.goal, ...prev]);
        setNewGoal({ goal_type: '', goal_text: '', goal_description: '', priority: 5 });
        setShowAddForm(false);
        if (onGoalChange) onGoalChange();
      } else {
        console.error('[XConnectGoals] API error:', response);
        setError(response?.error || 'Failed to add goal. Please try again.');
      }
    } catch (err) {
      console.error('[XConnectGoals] Error adding goal:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to add goal';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle goal status using unified /api/goals/:id/status endpoint
  const handleToggleGoal = async (goalId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    try {
      const response = await api.patch(`/goals/${goalId}/status`, {
        status: newStatus
      });

      if (response?.success) {
        setGoals(prev => prev.map(g =>
          g.id === goalId ? { ...g, status: newStatus } : g
        ));
        if (onGoalChange) onGoalChange();
      } else {
        console.error('[XConnectGoals] Toggle error:', response);
        setError(response?.error || 'Failed to update goal');
      }
    } catch (err) {
      console.error('[XConnectGoals] Error toggling goal:', err);
      setError(err.message || 'Failed to update goal');
    }
  };

  const categories = getGoalTypesForEntity(entityType);
  // Map profile_goals status to active/inactive display
  const activeGoals = goals.filter(g => g.status === 'active');
  const inactiveGoals = goals.filter(g => g.status !== 'active');

  if (loading) {
    return (
      <div className="xconnect-goals-loading">
        <Loader className="spin" size={24} />
        <span>Loading goals...</span>
      </div>
    );
  }

  return (
    <div className="xconnect-goals">
      <div className="xconnect-goals-header">
        <div className="header-title">
          <Target size={20} />
          <h3>My Goals</h3>
          <span className="header-subtitle">(synced with Workspace)</span>
        </div>
        <button
          className="add-goal-btn"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? <X size={16} /> : <Plus size={16} />}
          {showAddForm ? 'Cancel' : 'Add Goal'}
        </button>
      </div>

      {error && (
        <div className="xconnect-error">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {showAddForm && (
        <form className="add-goal-form" onSubmit={handleAddGoal}>
          <div className="form-group">
            <label>Goal Type *</label>
            <select
              value={newGoal.goal_type}
              onChange={(e) => setNewGoal(prev => ({ ...prev, goal_type: e.target.value }))}
              required
            >
              <option value="">Select a goal type...</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Goal Title *</label>
            <input
              type="text"
              value={newGoal.goal_text}
              onChange={(e) => setNewGoal(prev => ({ ...prev, goal_text: e.target.value }))}
              placeholder="e.g., Find a senior developer role"
              required
            />
          </div>

          <div className="form-group">
            <label>Description (optional)</label>
            <textarea
              value={newGoal.goal_description}
              onChange={(e) => setNewGoal(prev => ({ ...prev, goal_description: e.target.value }))}
              placeholder="Add more details about your goal..."
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>Priority: {newGoal.priority}/10</label>
            <input
              type="range"
              min="1"
              max="10"
              value={newGoal.priority}
              onChange={(e) => setNewGoal(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
              className="priority-slider"
            />
          </div>

          <button type="submit" className="submit-goal-btn" disabled={submitting}>
            {submitting ? 'Adding...' : 'Add Goal'}
          </button>
        </form>
      )}

      {goals.length === 0 ? (
        <div className="no-goals-state">
          <Target size={32} className="empty-icon" />
          <h4>No goals yet</h4>
          <p>Create a goal to start receiving personalized community suggestions</p>
          <button onClick={() => setShowAddForm(true)} className="create-first-goal-btn">
            <Plus size={16} />
            Create Your First Goal
          </button>
        </div>
      ) : (
        <>
          {activeGoals.length > 0 && (
            <div className="goals-section">
              <h4 className="section-label">Active Goals ({activeGoals.length})</h4>
              <div className="goals-list">
                {activeGoals.map(goal => (
                  <GoalItem
                    key={goal.id}
                    goal={goal}
                    onToggle={() => handleToggleGoal(goal.id, goal.status)}
                  />
                ))}
              </div>
            </div>
          )}

          {inactiveGoals.length > 0 && (
            <div className="goals-section inactive">
              <h4 className="section-label">Paused/Other Goals ({inactiveGoals.length})</h4>
              <div className="goals-list">
                {inactiveGoals.map(goal => (
                  <GoalItem
                    key={goal.id}
                    goal={goal}
                    onToggle={() => handleToggleGoal(goal.id, goal.status)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Format relative date/time for goals
const formatGoalTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

// Goal Item Component - uses profile_goals field names
const GoalItem = ({ goal, onToggle }) => {
  const getTypeLabel = (goalType) => {
    for (const entityCategories of Object.values(goalTypesByEntity)) {
      const found = entityCategories.find(c => c.value === goalType);
      if (found) return found.label;
    }
    return goalType || 'Custom';
  };

  const isActive = goal.status === 'active';

  return (
    <div className={`goal-item ${isActive ? 'active' : 'inactive'}`}>
      <div className="goal-item-main">
        <div className="goal-icon">
          <Target size={16} />
        </div>
        <div className="goal-info">
          <span className="goal-title">{goal.goal_text}</span>
          <div className="goal-meta">
            <span className="goal-category">{getTypeLabel(goal.goal_type)}</span>
            {goal.vertical && <span className="goal-vertical">{goal.vertical}</span>}
            {goal.priority && <span className="goal-priority">Priority: {goal.priority}/10</span>}
          </div>
          {goal.goal_description && (
            <p className="goal-description">{goal.goal_description}</p>
          )}
          {goal.created_at && (
            <span className="goal-time">{formatGoalTime(goal.created_at)}</span>
          )}
        </div>
      </div>
      <label className="goal-toggle">
        <input
          type="checkbox"
          checked={isActive}
          onChange={onToggle}
        />
        <span className="toggle-slider"></span>
      </label>
    </div>
  );
};

export default XConnectGoals;
